import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { PostStage, PostScheduleType } from '@/graphql/generated/types.generated';
import { useMemo, useEffect, useRef, useState } from 'react';
import { CreateRounded } from '@mui/icons-material';
import { PostTypeNumberNameKey } from '@/utils/posts/post-type-name-key';
import { PostTypeNumber } from '@/utils/posts/post-type-mapper';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { useQuery } from '@apollo/client';
import { PostDocument } from '@/graphql/generated/post.generated';
import { useAndie } from '@/providers/andie-provider';
import { enqueueSnackbar } from 'notistack';

type ActionResponse = { id: string };
type ActionArgs = {
    postId: string;
    post: {
        stage: PostStage;
        type: PostTypeNumber;
        scheduleType: PostScheduleType;
        publishAt: Date | null;
        netProfileAuthIds: string[];
        briefingVariableUuid?: string;
        bodiesVariableUuids?: { netId?: string | null; variableUuid: string }[];
        medias?: { netId?: string | null; order: number; mediaLibId: string }[];
    };
};

export type AndieUpdatePostProps = ActionRenderAi<ActionResponse, ActionArgs>;

const AndieUpdatePost = (props: AndieUpdatePostProps) => {
    const { t, i18n } = useTranslation();
    const listFormatter = useMemo(
        () => new Intl.ListFormat(i18n.language, { type: 'conjunction' }),
        [i18n.language],
    );

    const [postId, setPostId] = useState<string | null>(null);
    const { helpers } = useAndie();
    const executingSteps = useRef<number>(0);

    useQuery(PostDocument, {
        skip: !postId,
        ...(postId && {
            variables: { postId },
        }),
        onCompleted: (data) => {
            if (data?.post) {
                helpers.updatePost(data.post);
            }
        },
        onError: (e) => {
            enqueueSnackbar(e.message);
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (props.status === ActionResponseStatusAi.EXECUTING) {
            executingSteps.current += 1;
        }

        if (
            props.status === ActionResponseStatusAi.COMPLETE &&
            props.result?.id &&
            executingSteps.current > 0
        ) {
            setPostId(props.result?.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.status]);

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.update-post.title')}
            icon={<CreateRounded fontSize="small" />}
            loadingMessages={(args) => [
                t('andie.tool.update-post.loading', {
                    postType: t(args?.post?.type ? PostTypeNumberNameKey[args?.post?.type] : ''),
                    listChanges: listFormatter.format([
                        ...(args?.post
                            ? [
                                  ...(args.post.briefingVariableUuid ? [t('andie.tool.post.briefing')] : []),
                                  ...(args.post.bodiesVariableUuids ? [t('andie.tool.post.body')] : []),
                                  ...(args.post.medias ? [t('andie.tool.post.media')] : []),
                              ]
                            : []),
                    ]),
                }),
            ]}
            completeMessage={({ args }) =>
                t('andie.tool.update-post.complete', {
                    postType: t(args?.post?.type ? PostTypeNumberNameKey[args?.post?.type] : ''),
                    listChanges: listFormatter.format([
                        ...(args?.post
                            ? [
                                  ...(args.post.briefingVariableUuid ? [t('andie.tool.post.briefing')] : []),
                                  ...(args.post.bodiesVariableUuids ? [t('andie.tool.post.body')] : []),
                                  ...(args.post.medias ? [t('andie.tool.post.media')] : []),
                              ]
                            : []),
                    ]),
                })
            }
            {...props}
        />
    );
};

export default AndieUpdatePost;
